import { useEffect, useMemo } from "react";
import CreateModel from "../../components/buttons/CreateModelButton";
import FilterSearch from "../../components/Filtering/FilterSearch";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import FilterRightBox from "../../components/Filtering/FilterRightBoxModel";
import SearchButton from "../../components/buttons/SearchButton";
import BreadCrumb from "../../components/HeaderComponents/BreadCrum";
import { useNavigate } from "react-router-dom";
import { useAuthRoles } from "../../hooks/useAuthRoles";
import "react-toastify/dist/ReactToastify.css";
import DataTable from "../../components/DataTable/DataTable";
import { useFilteredData } from "../../hooks/useFilteredData";
import { useTableControls } from "../../hooks/useTableControls";
import { useWorkerApiHelper } from "../../hooks/useWorkerApiHelper";
import { Spinner } from "../../components/Filtering/Reusable/Spinner";
import { getTextByLang } from "../../utils/formUtils";
import { formatDate } from "../../utils/constants";
import { showErrorAlert } from "../../utils/showErrorAlert";

import { useDeleteVocab } from "../../hooks/vocabularies/useDeleteVocab";
import { useGetVocabsPaginated } from "../../hooks/vocabularies/useGetVocab";

function DataModel() {
  const { callApiWithWorker } = useWorkerApiHelper();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const {
    tableData,
    searchTerm,
    pendingSearchTerm,
    filters,
    open,
    pagination,
    rowCount,
    orgProvider,
    setTableData,
    setPagination,
    setRowCount,
    handleSearch,
    handleClearSearch,
    handleSearchSubmit,
    handleSearchKeyDown,
    handleClose,
    handleApplyFilters,
  } = useTableControls([]);

  const { isProvider, isAdmin } = useAuthRoles();
  const canShowMutationActions = isAdmin || orgProvider;

  const {
    data: datasetsPage,
    isFetching: isFetchingDatasets,
    isError: isDatasetsError,
    error: datasetsError,
  } = useGetVocabsPaginated({
    callApiWithWorker,
    currentLang: i18n.language,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
  });

  const { mutateAsync: deleteResource, isPending: isDeleting } = useDeleteVocab(
    {
      callApiWithWorker,
      currentLang: i18n.language,
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      t,
    },
  );

  useEffect(() => {
    const content = datasetsPage?.content || [];
    const total = datasetsPage?.totalElements || 0;

    setRowCount(total);

    const normalized = content.map((item) => {
      const rawResource =
        Array.isArray(item?.items) && item.items.length > 0
          ? item.items[0]
          : item;
      const metadata = rawResource?.metadata ?? {};

      return {
        ...item,
        ...rawResource,
        metadata,
        identifier: rawResource?.identifier ?? item?.identifier ?? "",
        publisher:
          rawResource?.publisher ??
          metadata?.publisher ??
          item?.publisher ??
          "",
        modified: rawResource?.modified ?? metadata?.modified ?? "",
        title: getTextByLang(rawResource?.title ?? item?.title),
        description: getTextByLang(
          metadata?.description ?? rawResource?.description,
        ),
        version_info:
          rawResource?.version_info ?? item?.version_info ?? "",
        type: rawResource?.type ?? item?.type ?? "",
      };
    });

    setTableData(normalized);
  }, [datasetsPage, i18n.language, setRowCount, setTableData]);

  useEffect(() => {
    if (!isDatasetsError) return;
    showErrorAlert(datasetsError);
  }, [isDatasetsError, datasetsError]);

  const handleEdit = (data) => {
    navigate(`/editresource/${data.identifier}`, {
      state: { record: data },
    });
  };

  const handleNameClick = (record) => {
    navigate(`/resourcedetail/${record.identifier}`, {
      state: { record },
    });
  };

  const handleDelete = async (row) => {
    try {
      const resourceId = row?.identifier || row?.fileHash || row?.id;

      if (!resourceId) {
        throw new Error("Missing fileHash/identifier for delete.");
      }

      const result = await Swal.fire({
        title: t("dataModels.deleteMessage"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: t("dataModels.deleteMessageYes"),
        cancelButtonText: t("dataModels.deleteMessageNo"),
      });

      if (!result.isConfirmed) return;

      await deleteResource(resourceId);
    } catch (error) {
      console.error("Error deleting resource:", error);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "modified",
        header: t("dataModels.discover.modified"),
        accessorFn: (row) => row?.modified ?? row?.metadata?.modified ?? "",
        meta: { width: "15%" },
        cell: ({ getValue }) => formatDate(getValue()),
      },
      {
        header: t("dataModels.discover.name"),
        accessorKey: "title",
        meta: { width: "25%" },
      },
      {
        header: t("dataModels.discover.description"),
        accessorKey: "description",
        meta: { width: "40%" },
      },
      {
        header: t("dataModels.discover.type"),
        accessorKey: "type",
        meta: { width: "10%" },
        cell: ({ getValue }) => {
          const value = getValue();
          let trimmed = value?.split("/").pop()?.trim() || "";
          if (!trimmed) return null;

          trimmed = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);

          return (
            <div className="centered-cell">
              <span className="type_dist">{trimmed}</span>
            </div>
          );
        },
      },
      {
        header: t("dataModels.discover.version"),
        accessorKey: "version_info",
        meta: { width: "10%" },
      },
    ],
    [t],
  );

  const filteredData = useFilteredData(tableData, searchTerm, filters, columns);

  const loading = isFetchingDatasets || isDeleting;

  return (
    <section className="pb4">
      <div className="w100 flexbread">
        <div className="divbreadcrum w90">
          <BreadCrumb />
        </div>
      </div>

      <div className="container containerflex">
        <div className="w90 labeltittle">
          <h2 className="tittlecatalog">{t("dataModels.title")}</h2>
          {isProvider && (
            <CreateModel onClick={() => navigate(`/createresource`)} />
          )}
        </div>

        <div className={"w86 filteractions pt3"}>
          <FilterSearch
            value={pendingSearchTerm}
            onChange={handleSearch}
            onClear={handleClearSearch}
            onKeyDown={handleSearchKeyDown}
          />
          <div className="plr2">
            <SearchButton onClick={handleSearchSubmit} />
          </div>
        </div>

        <div>
          <FilterRightBox
            open={open}
            onClose={handleClose}
            onApplyFilters={handleApplyFilters}
          />
        </div>

        <div className={`w90 bgwhite mtb2`}>
          <div className={`plr3 ptb`}>
            <DataTable
              data={filteredData}
              columns={columns}
              onEdit={(row) => handleEdit(row)}
              onDelete={(row) => handleDelete(row)}
              showActions={canShowMutationActions}
              onRowClick={handleNameClick}
              initialSorting={[{ id: "modified", desc: true }]}
              pagination={pagination}
              setPagination={setPagination}
              rowCount={rowCount}
            />
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading">
          <Spinner size="large" color="var(--main-color)" />
        </div>
      )}
    </section>
  );
}

export default DataModel;
